'use client';
import React, { useState, useRef } from 'react';
import { Upload, FileText, CheckCircle, Clock, AlertCircle, Eye, Trash2, ShieldCheck, Home, Scale,  } from 'lucide-react';

type DocCategory = 'ownership' | 'exemption' | 'dispute';
type UploadStatus = 'uploaded' | 'under_review' | 'approved' | 'rejected';

interface UploadedDoc {
  id: string;
  name: string;
  category: DocCategory;
  size: string;
  uploadedAt: string;
  status: UploadStatus;
  note?: string;
}

const categoryConfig: Record<DocCategory, { label: string; icon: React.ReactNode; color: string; bg: string; description: string; accepts: string[] }> = {
  ownership: {
    label: 'Ownership Deed',
    icon: <Home size={16} />,
    color: '#60a5fa',
    bg: 'rgba(96,165,250,0.12)',
    description: 'Property deed, title certificate, or land ownership document',
    accepts: ['PDF', 'JPG', 'PNG', 'TIFF'],
  },
  exemption: {
    label: 'Exemption Certificate',
    icon: <ShieldCheck size={16} />,
    color: '#34d399',
    bg: 'rgba(52,211,153,0.12)',
    description: 'Government exemption letter, NGO status, or diplomatic immunity certificate',
    accepts: ['PDF', 'JPG', 'PNG'],
  },
  dispute: {
    label: 'Dispute Evidence',
    icon: <Scale size={16} />,
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.12)',
    description: 'Supporting documents for assessment appeal or billing dispute',
    accepts: ['PDF', 'JPG', 'PNG', 'DOCX', 'XLSX'],
  },
};

const statusConfig: Record<UploadStatus, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  uploaded: { label: 'Uploaded', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', icon: <Clock size={12} /> },
  under_review: { label: 'Under Review', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', icon: <Clock size={12} /> },
  approved: { label: 'Approved', color: '#34d399', bg: 'rgba(52,211,153,0.12)', icon: <CheckCircle size={12} /> },
  rejected: { label: 'Rejected', color: '#f87171', bg: 'rgba(248,113,113,0.12)', icon: <AlertCircle size={12} /> },
};

const INITIAL_DOCS: UploadedDoc[] = [
  { id: 'ud-001', name: 'Property_Deed_Lot_47B.pdf', category: 'ownership', size: '2.4 MB', uploadedAt: '2026-06-12', status: 'approved' },
  { id: 'ud-002', name: 'Exemption_Certificate_2026.pdf', category: 'exemption', size: '890 KB', uploadedAt: '2026-06-20', status: 'under_review' },
  { id: 'ud-003', name: 'Dispute_Evidence_Assessment_Q1.pdf', category: 'dispute', size: '1.1 MB', uploadedAt: '2026-07-01', status: 'uploaded', note: 'Awaiting LRA officer assignment' },
];

export default function DocumentUploadTab() {
  const [docs, setDocs] = useState<UploadedDoc[]>(INITIAL_DOCS);
  const [activeCategory, setActiveCategory] = useState<DocCategory>('ownership');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | UploadStatus>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      processFiles(Array.from(e.target.files));
    }
  };

  const processFiles = (files: File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    setTimeout(() => {
      const newDocs: UploadedDoc[] = files.map((f, i) => ({
        id: `ud-new-${Date.now()}-${i}`,
        name: f.name,
        category: activeCategory,
        size: f.size > 1024 * 1024 ? `${(f.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(f.size / 1024)} KB`,
        uploadedAt: new Date().toISOString().split('T')[0],
        status: 'uploaded',
      }));
      setDocs((prev) => [...newDocs, ...prev]);
      setUploading(false);
    }, 1400);
  };

  const removeDoc = (id: string) => {
    setDocs((prev) => prev.filter((d) => d.id !== id));
  };

  const filtered = docs.filter((d) => filterStatus === 'all' || d.status === filterStatus);

  const counts = {
    total: docs.length,
    approved: docs.filter((d) => d.status === 'approved').length,
    under_review: docs.filter((d) => d.status === 'under_review').length,
    uploaded: docs.filter((d) => d.status === 'uploaded').length,
  };

  return (
    <div className="space-y-5">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Documents', value: counts.total, color: 'var(--accent)' },
          { label: 'Approved', value: counts.approved, color: '#34d399' },
          { label: 'Under Review', value: counts.under_review, color: '#fbbf24' },
          { label: 'Pending Upload', value: counts.uploaded, color: '#94a3b8' },
        ].map((k) => (
          <div
            key={k.label}
            className="rounded-xl px-4 py-3"
            style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}
          >
            <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{k.label}</p>
            <p className="text-2xl font-bold font-mono-data" style={{ color: k.color }}>{k.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Upload Panel */}
        <div className="lg:col-span-2 space-y-3">
          {/* Category selector */}
          <div
            className="rounded-xl p-4"
            style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
          >
            <p className="text-xs font-semibold mb-3" style={{ color: 'var(--dark-muted)' }}>
              DOCUMENT CATEGORY
            </p>
            <div className="space-y-2">
              {(Object.entries(categoryConfig) as [DocCategory, typeof categoryConfig[DocCategory]][]).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  className="w-full flex items-start gap-3 p-3 rounded-lg text-left transition-all"
                  style={{
                    backgroundColor: activeCategory === key ? cfg.bg : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${activeCategory === key ? cfg.color + '60' : 'var(--dark-border)'}`,
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: cfg.bg, color: cfg.color }}
                  >
                    {cfg.icon}
                  </div>
                  <div>
                    <p className="text-xs font-semibold" style={{ color: activeCategory === key ? cfg.color : 'var(--dark-text)' }}>
                      {cfg.label}
                    </p>
                    <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
                      {cfg.description}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--dark-muted)' }}>
                      Accepts: {cfg.accepts.join(', ')}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Drop zone */}
          <div
            className="rounded-xl p-5 text-center cursor-pointer transition-all"
            style={{
              backgroundColor: dragging ? 'rgba(212,160,23,0.08)' : 'var(--dark-card)',
              border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--dark-border)'}`,
            }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.tiff,.docx,.xlsx"
              onChange={handleFileSelect}
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <svg className="animate-spin w-8 h-8" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="var(--accent)" strokeWidth="3" />
                  <path className="opacity-75" fill="var(--accent)" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <p className="text-sm font-medium" style={{ color: 'var(--accent)' }}>Uploading…</p>
              </div>
            ) : (
              <>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                  style={{ backgroundColor: 'rgba(212,160,23,0.12)' }}
                >
                  <Upload size={22} style={{ color: 'var(--accent)' }} />
                </div>
                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--dark-text)' }}>
                  Drop files here or click to browse
                </p>
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                  Uploading as: <span style={{ color: categoryConfig[activeCategory].color }}>{categoryConfig[activeCategory].label}</span>
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--dark-muted)' }}>
                  Max 10 MB per file
                </p>
              </>
            )}
          </div>
        </div>

        {/* Document List */}
        <div className="lg:col-span-3">
          <div
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
          >
            <div
              className="flex items-center justify-between px-4 py-3 border-b"
              style={{ borderColor: 'var(--dark-border)' }}
            >
              <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
                Uploaded Documents
              </p>
              <div className="flex gap-1.5">
                {(['all', 'approved', 'under_review', 'uploaded'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                    style={{
                      backgroundColor: filterStatus === s ? 'rgba(212,160,23,0.15)' : 'var(--dark-elevated)',
                      color: filterStatus === s ? 'var(--accent)' : 'var(--dark-muted)',
                      border: `1px solid ${filterStatus === s ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                    }}
                  >
                    {s === 'all' ? 'All' : s === 'under_review' ? 'Review' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="divide-y" style={{ borderColor: 'var(--dark-border)' }}>
              {filtered.length === 0 ? (
                <div className="py-12 text-center">
                  <FileText size={32} className="mx-auto mb-3 opacity-30" style={{ color: 'var(--dark-muted)' }} />
                  <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>No documents found</p>
                </div>
              ) : (
                filtered.map((doc) => {
                  const cat = categoryConfig[doc.category];
                  const st = statusConfig[doc.status];
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 px-4 py-3 transition-colors"
                      style={{ borderColor: 'var(--dark-border)' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.02)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: cat.bg, color: cat.color }}
                      >
                        {cat.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate" style={{ color: 'var(--dark-text)' }}>
                          {doc.name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span
                            className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                            style={{ backgroundColor: cat.bg, color: cat.color }}
                          >
                            {cat.label}
                          </span>
                          <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                            {doc.size} · {doc.uploadedAt}
                          </span>
                        </div>
                        {doc.note && (
                          <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                            {doc.note}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span
                          className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{ backgroundColor: st.bg, color: st.color }}
                        >
                          {st.icon}
                          {st.label}
                        </span>
                        <button
                          className="p-1.5 rounded-lg transition-all hover:bg-white/10"
                          style={{ color: 'var(--dark-muted)' }}
                          title="Preview"
                          aria-label={`Preview ${doc.name}`}
                        >
                          <Eye size={13} />
                        </button>
                        {doc.status === 'uploaded' && (
                          <button
                            onClick={() => removeDoc(doc.id)}
                            className="p-1.5 rounded-lg transition-all hover:bg-red-500/10"
                            style={{ color: '#f87171' }}
                            title="Remove"
                            aria-label={`Remove ${doc.name}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Verification notice */}
          <div
            className="mt-3 rounded-xl p-4 flex items-start gap-3"
            style={{ backgroundColor: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.2)' }}
          >
            <ShieldCheck size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#60a5fa' }} />
            <div>
              <p className="text-xs font-semibold mb-0.5" style={{ color: '#60a5fa' }}>
                Stage 3 — Owner & Taxpayer Verification
              </p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
                Uploaded documents are reviewed by LRA officers within 3–5 business days. Approved ownership deeds and exemption certificates are linked to your property record. Dispute evidence is forwarded to the Appeals Unit.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
